import { Trial_Status, useStartTrialMutation } from '@generated/graphql';
import { Box, Button, Grid2, Stack, Typography } from '@mui/material';
import { LabelBox, useToast, ValueBox } from '@myhrbox/myhrbox-frontend-core';
import MicVolumeModal from 'components/modals/MicVolumeModal';
import { useUnit } from 'effector-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  $assessmentInfo,
  $isPublicExamination,
  nextPage,
  setAssessmentInfo,
  setAssessmentStatus,
  setPage,
} from 'store/assessment/model';
import { AssessmentInfo, AssessmentPage } from 'store/assessment/types';

const Instruction = () => {
  const [assessmentInfo, isPublicExamination] = useUnit([$assessmentInfo, $isPublicExamination]);
  const { showErrorToast } = useToast();
  const [micVolumeModalOpen, setMicVolumeModalOpen] = useState(false);
  const { t } = useTranslation();
  const recommendations = t('assessment.instruction.recommendations.points', {
    returnObjects: true,
  });
  const specificities = t('assessment.instruction.specificities.points', {
    returnObjects: true,
  });

  const { id } = useParams<{ id: string }>();

  const totalTime = assessmentInfo?.totalTime || 0;

  const [trialStart, { loading: trialStartLoading }] = useStartTrialMutation();

  const onStartTrial = () => {
    trialStart({
      variables: {
        trialId: id!,
      },
    })
      .then((res) => {
        const { status, sessionId } = res.data?.startTrial || {};
        if (status === Trial_Status.Started) {
          setAssessmentInfo({ ...(assessmentInfo as AssessmentInfo), status, sessionId });
          setAssessmentStatus(status);

          if (assessmentInfo?.examination.isInterview) {
            setPage(AssessmentPage.Question);
          } else {
            nextPage();
          }
        } else {
          showErrorToast(t('errors.common'));
        }
      })
      .catch(() => showErrorToast(t('errors.common')));
  };

  return (
    <>
      <Stack spacing={3}>
        <Typography variant='h28'>
          {isPublicExamination
            ? t('assessment.instruction.publicTitle')
            : t('assessment.instruction.title')}
        </Typography>
        <Stack spacing={{ xs: 2.5, sm: 1.8 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }}>
            <LabelBox
              sx={{ wordBreak: 'normal' }}
              text={t('assessment.instruction.competencyMapTitle')}
            />
            <ValueBox
              text={assessmentInfo?.examination.competencyMap.title || ''}
              sx={{ width: '100%' }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }}>
            <LabelBox text={t('assessment.instruction.assessmentDuration')} />
            <ValueBox
              text={t('time.minute', { count: totalTime / 60, format: 'minute' })}
              sx={{ width: '100%', flex: 1 }}
            />
          </Stack>
        </Stack>
        <Stack spacing={1}>
          <Typography variant='body14rg'>{t('assessment.instruction.about')}</Typography>
          <Typography variant='body14rg'>{t('assessment.instruction.description')}</Typography>
          <Typography variant='body14rg'>{t('assessment.instruction.note')}</Typography>
          <Typography variant='body14rg' sx={{ marginTop: '12px !important' }}>
            {t('assessment.instruction.specificities.general')}
          </Typography>
          <Box component='ul' sx={{ m: '0 !important', pl: 3 }}>
            {specificities.map((point, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={`recommendation-${index}`}>
                <Typography variant='body14rg'>{point}</Typography>
              </li>
            ))}
          </Box>
          <Typography variant='body14rg' sx={{ marginTop: '12px !important' }}>
            {t('assessment.instruction.recommendations.general')}
          </Typography>
          <Box component='ul' sx={{ m: '0 !important', pl: 3 }}>
            {recommendations.map((point, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={`recommendation-${index}`}>
                <Typography variant='body14rg'>{point}</Typography>
              </li>
            ))}
          </Box>
          <Typography variant='body14rg' sx={{ marginTop: '12px !important' }}>
            {t('assessment.instruction.final_message')}
          </Typography>
        </Stack>
        <Grid2 container spacing={2} justifyContent='end'>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Button
              sx={{ height: 45 }}
              variant='outlined'
              size='large'
              fullWidth
              onClick={() => setMicVolumeModalOpen(true)}>
              {t('microphone.check')}
            </Button>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Button
              loading={trialStartLoading}
              variant='contained'
              size='large'
              fullWidth
              onClick={onStartTrial}>
              {t('assessment.instruction.start')}
            </Button>
          </Grid2>
        </Grid2>
      </Stack>
      <MicVolumeModal open={micVolumeModalOpen} onClose={() => setMicVolumeModalOpen(false)} />
    </>
  );
};

export default Instruction;
